/**
 * Maps technical error messages to user-friendly messages for campaign failures.
 *
 * This utility provides clear, actionable error messages for users when campaigns fail,
 * hiding technical stack traces and implementation details.
 */

interface ErrorPattern {
  pattern: RegExp | string;
  userMessage: string | ((errorMessage: string) => string);
}

/**
 * Common error patterns and their user-friendly messages.
 * Patterns are checked in order, first match wins.
 */
const ERROR_PATTERNS: ErrorPattern[] = [
  // Website/scraping errors
  {
    pattern: /website.*not.*accessible|failed.*to.*scrape|scraping.*failed/i,
    userMessage: "We couldn't access your website. Please check that the URL is correct and publicly accessible.",
  },
  {
    pattern: /invalid.*url|malformed.*url/i,
    userMessage: "The website URL appears to be invalid. Please check the URL format.",
  },
  {
    pattern: /timeout|timed.*out/i,
    userMessage: "The request took too long to complete. This might be a temporary issue - please try again.",
  },

  // AI/LLM errors
  {
    pattern: /failed.*to.*generate.*headlines|headline.*generation.*failed/i,
    userMessage: "We encountered an issue generating ad headlines. Please try again.",
  },
  {
    pattern: /failed.*to.*generate.*descriptions|description.*generation.*failed/i,
    userMessage: "We encountered an issue generating ad descriptions. Please try again.",
  },
  {
    pattern: /failed.*to.*generate.*keywords|keyword.*generation.*failed/i,
    userMessage: "We encountered an issue generating keywords. Please try again.",
  },
  {
    pattern: /ai.*service.*unavailable|llm.*error/i,
    userMessage: "Generation is temporarily unavailable. Please try again in a few moments.",
  },

  // Google Ads API errors
  {
    pattern: /google.*ads.*api.*error|googleads.*exception/i,
    userMessage: "We encountered an issue connecting to Google Ads. Please try again.",
  },
  {
    pattern: /authorization.*failed|authentication.*failed|refresh.*token/i,
    userMessage: "Your Google Ads connection needs to be renewed. Please reconnect your account.",
  },
  {
    pattern: /budget.*too.*low|insufficient.*budget/i,
    userMessage: "The campaign budget is too low. Please increase the budget and try again.",
  },
  {
    pattern: /rate.*limit|too.*many.*requests/i,
    userMessage: "We're experiencing high traffic. Please wait a moment and try again.",
  },

  // Database/network errors
  {
    pattern: /database.*error|connection.*error/i,
    userMessage: "We encountered a temporary connection issue. Please try again.",
  },
  {
    pattern: /network.*error/i,
    userMessage: "Network connectivity issue detected. Please check your connection and try again.",
  },

  // Location/geo errors
  {
    pattern: /target location.*not found|geo target data may not be seeded/i,
    userMessage: "One or more of your selected locations could not be found. Please try again or contact support.",
  },

  // Business logic errors
  {
    pattern: /no.*content.*found|insufficient.*content/i,
    userMessage: "We couldn't find enough content on your website to create ads. Please ensure your website has sufficient text content.",
  },
  {
    pattern: /missing.*business.*id/i,
    userMessage: "Business information is missing. Please complete your business profile.",
  },
  {
    pattern: /invalid.*campaign.*type/i,
    userMessage: "Invalid campaign configuration. Please contact support.",
  },
  
  // #4601 — Ad Incomplete / REBUILD AD flow deprecated
  // {
  //   pattern: /missing.*essential.*data/i,
  //   userMessage: (errorMessage: string) => {
  //     const keywordMatch = errorMessage.match(/Keywords:\s*(\d+)/i);
  //     const headlineMatch = errorMessage.match(/Headlines:\s*(\d+)/i);
  //     const descriptionMatch = errorMessage.match(/Descriptions:\s*(\d+)/i);
  //     const keywordCount = keywordMatch ? parseInt(keywordMatch[1]) : 0;
  //     const headlineCount = headlineMatch ? parseInt(headlineMatch[1]) : 0;
  //     const descriptionCount = descriptionMatch ? parseInt(descriptionMatch[1]) : 0;
  //     const missingItems: string[] = [];
  //     if (keywordCount === 0) missingItems.push('keywords');
  //     if (headlineCount === 0) missingItems.push('headlines');
  //     if (descriptionCount === 0) missingItems.push('descriptions');
  //     if (missingItems.length === 0) return "This ad is missing some essential data. Click REBUILD AD to regenerate it.";
  //     if (missingItems.length === 1) return `This ad is missing ${missingItems[0]}. Click REBUILD AD to regenerate it.`;
  //     if (missingItems.length === 2) return `This ad is missing ${missingItems[0]} and ${missingItems[1]}. Click REBUILD AD to regenerate them.`;
  //     return "This ad is missing keywords, headlines, and descriptions. Click REBUILD AD to regenerate them.";
  //   },
  // },
];

/**
 * Default message when no pattern matches
 */
const DEFAULT_ERROR_MESSAGE =
  "We encountered an unexpected issue while creating your campaign. Please try again or contact support if the problem persists.";

/**
 * Maps a technical error message to a user-friendly message.
 *
 * @param errorMessage - The technical error message from the backend
 * @returns A user-friendly error message
 */
export function mapErrorToUserMessage(errorMessage: string | null | undefined): string {
  if (!errorMessage) {
    return DEFAULT_ERROR_MESSAGE;
  }

  // Check each pattern
  for (const { pattern, userMessage } of ERROR_PATTERNS) {
    let matches = false;
    
    if (typeof pattern === 'string') {
      matches = errorMessage.toLowerCase().includes(pattern.toLowerCase());
    } else {
      matches = pattern.test(errorMessage);
    }
    
    if (matches) {
      // If userMessage is a function, call it with the error message
      if (typeof userMessage === 'function') {
        return userMessage(errorMessage);
      }
      // Otherwise, return the string directly
      return userMessage;
    }
  }

  // No pattern matched, return default
  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Determines if an error is likely user-fixable
 * (vs requiring support intervention)
 */
export function isErrorUserFixable(errorMessage: string | null | undefined): boolean {
  if (!errorMessage) {
    return false;
  }

  const userFixablePatterns = [
    /website.*not.*accessible/i,
    /invalid.*url/i,
    /budget.*too.*low/i,
    /authorization.*failed/i,
    /insufficient.*content/i,
  ];

  return userFixablePatterns.some(pattern => pattern.test(errorMessage));
}
